import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ListButton, SaveButton } from "@refinedev/antd";
import {
  Collapse,
  ColorPicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  Result,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadProps } from "antd";
import { CheckOutlined, LeftOutlined, PlusOutlined, ToolOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { getErrorMessage } from "@/utilities";
import { PhoneNumberInput } from "@/components/phone-number-input";
import { getSellerSettings, updateSellerSettings } from "./api";

const { TextArea } = Input;

export const SellerSettingsList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const sellerCode = decodeURIComponent(id ?? "");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  /**
   * Por qué no se pudo cargar, o null si cargó bien.
   *
   * El form arranca vacío, así que si la carga falla y se deja editable, quien tenga
   * permiso de escritura puede completar lo obligatorio, guardar, y vaciar en el
   * camino los campos opcionales que no llegó a ver (dirección, horario, texto
   * adicional): el PATCH escribe lo que le manden, y un string vacío es un valor.
   * No es hipotético — se reprodujo. Sin datos, el form no se edita.
   */
  const [loadError, setLoadError] = useState<"forbidden" | "error" | null>(null);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | undefined>();
  const [squareLogoUrl, setSquareLogoUrl] = useState<string | undefined>();

  const [mapQuery, setMapQuery] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    fetch(`${API_BASE_URL}/sellers/regions`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setRegions(Array.isArray(data) ? data : []))
      .catch(() => setRegions([]));
  }, []);

  useEffect(() => {
    if (!sellerCode) return;
    setLoading(true);
    getSellerSettings(sellerCode)
      .then((data) => {
        form.setFieldsValue(data);
        // El preview usa la URL absoluta que deriva el backend; `data.logo` es el
        // path relativo de Magento y el navegador lo resolvería contra el host del
        // panel, que no sirve la media. El fallback deja el comportamiento viejo
        // si el API todavía no expone el campo.
        setLogoUrl(data.logoUrl ?? data.logo);
        setSquareLogoUrl(data.squareLogoUrl ?? data.squareLogo);
        setMapQuery(data.street || (data.latitude && data.longitude ? `${data.latitude},${data.longitude}` : ""));
      })
      .catch((e: any) => {
        // El 403 es una respuesta legítima del backend —el operador no tiene
        // `seller_config`—, no una falla. Merece otro mensaje: decirle "no se pudo
        // cargar" manda a buscar un problema técnico que no existe.
        const forbidden = e?.response?.status === 403;
        setLoadError(forbidden ? "forbidden" : "error");
        message.error(
          forbidden
            ? "No tenés permisos para ver la configuración de este seller"
            : "No se pudo cargar la configuración del seller",
        );
      })
      .finally(() => setLoading(false));
  }, [sellerCode, form]);

  const makeLogoUpload = (
    attribute: "logo" | "square_logo",
    fieldName: "logo" | "squareLogo",
    setUrl: (url: string) => void,
  ): UploadProps => ({
    accept: "image/*",
    maxCount: 1,
    showUploadList: false,
    customRequest: async ({ file, onSuccess, onError }) => {
      const fd = new FormData();
      fd.append("file", file as Blob);
      fd.append("attribute", attribute);
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE_URL}/sellers/logo`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: fd,
        });
        if (!res.ok) {
          const raw = await res.text();
          let detail = raw;
          try {
            const parsed = JSON.parse(raw);
            const msg = parsed?.message ?? parsed?.error;
            detail = Array.isArray(msg) ? msg.join(" · ") : (msg ?? raw);
          } catch {
            // body no-JSON → dejamos el texto tal cual
          }
          throw new Error(detail || `Error ${res.status}`);
        }
        const data = await res.json();
        // Al form va el path relativo, que es lo que persiste la columna; al
        // preview, la URL absoluta.
        form.setFieldValue(fieldName, data.path);
        setUrl(data.url ?? data.path);
        message.success("Imagen subida");
        onSuccess?.(data);
      } catch (e: any) {
        message.error(`No se pudo subir la imagen: ${getErrorMessage(e)}`);
        onError?.(e);
      }
    },
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (values.mainColor && typeof values.mainColor !== "string" && values.mainColor.toHexString) {
        values.mainColor = values.mainColor.toHexString();
      }
      setSaving(true);
      await updateSellerSettings(sellerCode, values);
      message.success("Configuración guardada");
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const UploadCard = ({ url, uploadProps }: { url?: string; uploadProps: UploadProps }) => (
    <Upload listType="picture-card" showUploadList={false} {...uploadProps}>
      {url ? (
        <img src={url} style={{ width: "100%", height: "100%", objectFit: "contain" }} alt="" />
      ) : (
        <Flex vertical align="center" gap={4}>
          <PlusOutlined />
          <span>Subir</span>
        </Flex>
      )}
    </Upload>
  );

  const collapseItems = [
    {
      key: "technical",
      label: <span style={{ fontWeight: 600 }}>Técnico</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item label="Código" name="code">
            <Input disabled />
          </Form.Item>
          <Form.Item label="Nombre del seller" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Color principal"
            name="mainColor"
            rules={[{ required: true }]}
            extra="Este color se usa para destacar al Seller en la interfaz."
          >
            <ColorPicker showText format="hex" />
          </Form.Item>
          <Form.Item
            label="Logo del seller"
            required
            extra="PNG o Webp transparente, 200×700px."
          >
            <Form.Item name="logo" noStyle>
              <Input type="hidden" />
            </Form.Item>
            <UploadCard url={logoUrl} uploadProps={makeLogoUpload("logo", "logo", setLogoUrl)} />
          </Form.Item>
          <Form.Item
            label="Logo cuadrado del seller"
            required
            extra="PNG o Webp transparente, 200×200px."
          >
            <Form.Item name="squareLogo" noStyle>
              <Input type="hidden" />
            </Form.Item>
            <UploadCard
              url={squareLogoUrl}
              uploadProps={makeLogoUpload("square_logo", "squareLogo", setSquareLogoUrl)}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "company",
      label: <span style={{ fontWeight: 600 }}>Información de empresa</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item label="Nombre de empresa" name="legalName" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="CUIT / RUT" name="taxvat" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="WhatsApp de ventas"
            name="whatsapp"
            rules={[{ required: true }]}
            extra="Dato de contacto público por defecto del Seller"
          >
            <PhoneNumberInput placeholder="WhatsApp" />
          </Form.Item>
          <Form.Item
            label="Teléfono de ventas"
            name="phone"
            extra="Dato de contacto público por defecto del Seller"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Email de ventas"
            name="email"
            rules={[{ required: true, type: "email" }]}
            extra="Dato de contacto público por defecto del Seller"
          >
            <Input />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "pickup",
      label: <span style={{ fontWeight: 600 }}>Punto de retiro por defecto</span>,
      styles: { header: { height: 40, alignItems: "center" } },
      children: (
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 15 }}
          style={{ maxWidth: 640 }}
          colon
        >
          <Form.Item
            label="Dirección"
            name="street"
            rules={[{ required: true }]}
            extra="Ingresá la dirección tal como aparece en Google Maps"
          >
            <Input
              placeholder="Ej: Av. Juan B Justo 1250"
              onChange={(e) => setMapQuery(e.target.value)}
            />
          </Form.Item>
          {mapQuery && (
            <Form.Item wrapperCol={{ offset: 9, span: 15 }}>
              <iframe
                key={mapQuery}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="200"
                style={{ border: 0, borderRadius: 8 }}
                loading="lazy"
                title="Mapa del punto de retiro"
              />
            </Form.Item>
          )}
          <Form.Item label="Ciudad" name="city" rules={[{ required: true }]}>
            <Select
              showSearch
              allowClear
              placeholder="Seleccione una ciudad"
              optionFilterProp="label"
              options={regions.map((r) => ({ value: r.name, label: r.name }))}
            />
          </Form.Item>
          <Form.Item label="Código postal" name="postcode" rules={[{ required: true }]}>
            <InputNumber controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Latitud"
            name="latitude"
            extra="El sistema usa latitud y longitud para calcular distancias."
          >
            <InputNumber controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Longitud" name="longitude">
            <InputNumber controls={false} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Horario" name="storeHours">
            <Input />
          </Form.Item>
          <Form.Item
            label="Texto adicional"
            name="extraInfo"
            extra="Info adicional para facilitar la ubicación del punto de retiro."
          >
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Flex vertical style={{ height: "100%", minHeight: 0 }}>
      {/* Header */}
      {/* Es una página del menú, no un detalle: va el ícono del menú y no la flecha de volver (xcons-docs#509). */}
      <Flex align="center" gap={8} style={{ marginBottom: 24, flexShrink: 0 }}>
        <ToolOutlined style={{ fontSize: 22 }} />
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t("settings.seller.title", "Configuración del Seller")}
        </Typography.Title>
      </Flex>

      {/* Form body */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <Spin spinning={loading}>
          {loadError ? (
            <Result
              status={loadError === "forbidden" ? "403" : "error"}
              title={
                loadError === "forbidden"
                  ? "No tenés permisos para ver esta configuración"
                  : "No se pudo cargar la configuración"
              }
              subTitle={
                loadError === "forbidden"
                  ? "Pedile a un administrador del seller el permiso de Configuración."
                  : "Volvé a intentar en un momento."
              }
            />
          ) : (
            <Collapse
              defaultActiveKey={["technical", "company", "pickup"]}
              items={collapseItems}
            />
          )}
        </Spin>
      </div>

      {/* Footer */}
      <div style={{ paddingTop: 32, paddingBottom: 16, flexShrink: 0 }}>
        <Flex justify="space-between">
          <ListButton icon={<LeftOutlined />} onClick={() => navigate(-1)}>
            {t("buttons.back", "Volver")}
          </ListButton>
          {!loadError && (
            <SaveButton loading={saving} onClick={handleSave} icon={<CheckOutlined />} iconPosition="end">
              {t("buttons.save", "Guardar")}
            </SaveButton>
          )}
        </Flex>
      </div>
    </Flex>
  );
};
